import { getTopRiskEvidence } from "../forecast-diagnosis/forecastDiagnosisContract";
import type { ForecastDiagnosisStory } from "../forecast-diagnosis/forecastDiagnosisStory";
import { categoryIndicators, mapRiskName, riskIndicatorGroupings, type RiskIndicator } from "../lib/risk-contracts";
import { getDealsForForecastSignal, type ForecastInsightDeal } from "../state-of-sales-v2/forecastInsights";

// Target Outlook is a Deal-level view and therefore reuses the canonical intrinsic
// Deal-risk indicators. Pipeline concentration and retired indicators stay out.
export function getOutlookDealRiskSignal(deal: Record<string, unknown> | null | undefined) {
  if (!deal?.risks || typeof deal.risks !== "object") return "Risk information is unavailable.";
  const indicators = categoryIndicators.forecast;
  const top = getTopRiskEvidence(deal, indicators);
  if (!top || top.score <= 0) return "No current risk signal is identified.";
  const observation = top.indicator === "lastModifiedDate"
    ? top.explanation?.replace(/^Last modified/i, "CRM record last updated") : top.explanation;
  return `Highest-scoring risk: ${mapRiskName(top.indicator)}${observation ? ` — ${observation}` : "."}`
    + (top.indicator === "lastModifiedDate" ? ". This measures CRM updates, not confirmed customer contact." : "");
}

export function getOutlookRiskMemberships(story: ForecastDiagnosisStory, deals: ForecastInsightDeal[]) {
  const contributions = new Map(story.deals.map(deal => [deal.id, deal.evidenceContribution]));
  // Preserve the existing diagnosis driver's eligibility; this change broadens
  // risk categories, not which deals qualify for diagnosis in the first place.
  const eligibleIds = new Set(story.deals.filter(deal => deal.baselineContribution > deal.evidenceContribution).map(deal => deal.id));
  // Canonical selected-period ARR is only an exposure measure. Risk points,
  // forecast outcomes and conditional upside remain untouched.
  const scoped = deals.filter(deal => eligibleIds.has(String(deal.id)))
    .map(deal => ({ ...deal, forecast_contribution: contributions.get(String(deal.id)) ?? 0 }));
  const observed = new Set(scoped.flatMap(deal => Object.keys(deal.risks ?? {})) as RiskIndicator[]);
  const indicators = categoryIndicators.forecast.filter(indicator => observed.has(indicator));
  const timing = new Set(riskIndicatorGroupings.dealProgression);
  const activity = new Set([...riskIndicatorGroupings.salesExecution, ...riskIndicatorGroupings.buyerMomentum]);
  const groups = [
    { id: "timing-and-deal-velocity", indicators: indicators.filter(key => timing.has(key)) },
    { id: "pipeline-momentum", indicators: indicators.filter(key => activity.has(key)) },
    { id: "other-deal-risks", indicators: indicators.filter(key => !timing.has(key) && !activity.has(key)) },
  ];
  return groups.map(group => ({ id: group.id,
    // Reuse the existing non-low-risk threshold and qualifying-deal selector.
    // selectOutlookDrivers keeps material amounts and assigns each deal once.
    dealIds: [...new Set(group.indicators.flatMap(indicator =>
      getDealsForForecastSignal(scoped, indicator).map(deal => String(deal.id))))],
  }));
}
